import {
  Badge,
  Button,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { Pencil, Trash2 } from "lucide-react";
import type { LeaveTypeListItem } from "@/modules/hr/leave/domain/types";

type LeaveTypesTableProps = {
  data: LeaveTypeListItem[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onView?: (row: LeaveTypeListItem) => void;
  onEdit: (row: LeaveTypeListItem) => void;
  onDelete?: (row: LeaveTypeListItem) => void;
};

export function LeaveTypesTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onView,
  onEdit,
  onDelete,
}: LeaveTypesTableProps) {
  const columns: TanstackReactTable.ColumnDef<LeaveTypeListItem>[] = [
    createSortableColumn<LeaveTypeListItem>("name", "ຊື່", {
      size: 180,
      cell: ({ row }) => (
        <div>
          <div>
            {onView ? (
              <button
                type="button"
                className="font-medium text-primary hover:underline"
                onClick={() => onView(row.original)}
              >
                {row.original.name}
              </button>
            ) : (
              <span className="font-medium">{row.original.name}</span>
            )}
          </div>
          <div className="text-muted-foreground text-xs">
            {row.original.code}
          </div>
        </div>
      ),
    }),
    createSortableColumn<LeaveTypeListItem>("isPaid", "ລາຮັບເງິນ", {
      size: 100,
      cell: ({ row }) => (
        <Badge variant={row.original.isPaid ? "default" : "secondary"}>
          {row.original.isPaid ? "ຮັບເງິນ" : "ບໍ່ຮັບເງິນ"}
        </Badge>
      ),
    }),
    createSortableColumn<LeaveTypeListItem>("maxDaysPerYear", "ວັນສູງສຸດ/ປີ", {
      size: 120,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.maxDaysPerYear ?? "—"}
        </span>
      ),
    }),
    createSortableColumn<LeaveTypeListItem>("allowCarryOver", "ນຳວັນໄປປີຖັດ", {
      size: 120,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.allowCarryOver ? "ອະນຸຍາດ" : "—"}
        </span>
      ),
    }),
    createSortableColumn<LeaveTypeListItem>("isActive", "ເປີດໃຊ້ງານ", {
      size: 100,
      cell: ({ row }) => (
        <Badge variant={row.original.isActive ? "default" : "secondary"}>
          {row.original.isActive ? "ໃຊ້ງານຢູ່" : "ປິດແລ້ວ"}
        </Badge>
      ),
    }),
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(row.original)}
            aria-label="ແກ້ໄຂປະເພດການລາ"
          >
            <Pencil className="h-4 w-4" />
          </Button>
          {onDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(row.original)}
              aria-label="ລຶບປະເພດການລາ"
              className="text-destructive hover:bg-destructive/10 hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
      size: 100,
    },
  ];

  return (
    <DataTable<LeaveTypeListItem, unknown>
      noDataMessage="ບໍ່ພົບປະເພດການລາ."
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id ?? "orderIndex", !!sorting[0]?.desc);
      }}
    />
  );
}
